import { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Typography,
} from "antd";
import { api } from "../../api/api";
import { useAuth } from "../../contexts/auth.context";
import { hasAdminAccess } from "../../utils/roleUtils";
import { ProduceGroupType } from "../../types/produceGroup.type";

const { Title } = Typography;

export default function ProduceGroups() {
  const { authState } = useAuth();
  const canEdit = hasAdminAccess(authState.user?.role);

  const [groups, setGroups] = useState<ProduceGroupType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedGroup, setSelectedGroup] = useState<ProduceGroupType | null>(null);

  const [form] = Form.useForm();

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const response = await api.get("/produce-group");
      setGroups(response.data);
    } catch (error: any) {
      console.error("Error fetching produce groups:", error);
      message.error(error.response?.data?.error || "Ошибка при получении групп.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const handleAddGroup = () => {
    form.resetFields();
    setSelectedGroup(null);
    setIsModalOpen(true);
  };

  const handleEditGroup = (group: ProduceGroupType) => {
    form.setFieldsValue({
      name: group.name,
      lowerLimitPercent: group.lowerLimitPercent,
      upperLimitPercent: group.upperLimitPercent,
      hideCableWeight: group.hideCableWeight ?? false,
    });
    setSelectedGroup(group);
    setIsModalOpen(true);
  };

  const handleFormSubmit = async (values: any) => {
    setConfirmLoading(true);
    try {
      if (selectedGroup) {
        await api.put(`/produce-group/${selectedGroup._id}`, values);
        message.success("Группа обновлена.");
      } else {
        await api.post("/produce-group", values);
        message.success("Группа добавлена.");
      }
      form.resetFields();
      setSelectedGroup(null);
      setIsModalOpen(false);
      fetchGroups();
    } catch (error: any) {
      console.error("Error saving produce group:", error);
      message.error(error.response?.data?.error || "Ошибка при сохранении группы.");
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleDeleteGroup = async (id: string) => {
    try {
      await api.delete(`/produce-group/${id}`);
      message.success("Группа удалена.");
      fetchGroups();
    } catch (error: any) {
      console.error("Error deleting produce group:", error);
      message.error(error.response?.data?.error || "Ошибка при удалении группы.");
    }
  };

  const columns = [
    {
      title: "Название",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Нижний лимит %",
      dataIndex: "lowerLimitPercent",
      key: "lowerLimitPercent",
      render: (value: number) => `−${value}%`,
    },
    {
      title: "Верхний лимит %",
      dataIndex: "upperLimitPercent",
      key: "upperLimitPercent",
      render: (value: number) => `+${value}%`,
    },
    {
      title: "Масса в прайсе",
      dataIndex: "hideCableWeight",
      key: "hideCableWeight",
      render: (value: boolean) => (value ? "Скрыта" : "Показана"),
    },
    ...(canEdit
      ? [
          {
            title: "Действия",
            key: "actions",
            render: (_: any, record: ProduceGroupType) => (
              <Space>
                <Button type="link" onClick={() => handleEditGroup(record)}>
                  Изменить
                </Button>
                <Popconfirm
                  title="Удалить группу?"
                  description="Группа будет снята со всей продукции."
                  okText="Да"
                  cancelText="Нет"
                  onConfirm={() => handleDeleteGroup(record._id)}
                >
                  <Button type="link" danger>
                    Удалить
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <Title level={2}>Группы продукции</Title>
      <Space style={{ marginBottom: 16 }} wrap>
        {canEdit && (
          <Button type="primary" onClick={handleAddGroup}>
            Добавить группу
          </Button>
        )}
      </Space>
      <Table
        columns={columns}
        dataSource={groups}
        rowKey="_id"
        loading={loading}
        pagination={false}
        scroll={{ x: "max-content" }}
      />
      <Modal
        title={selectedGroup ? "Изменить группу" : "Добавить группу"}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedGroup(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleFormSubmit} layout="vertical">
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, message: "Введите название" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="lowerLimitPercent"
            label="Нижний лимит % (допуск вниз от ожидаемого)"
            rules={[{ required: true, message: "Введите нижний лимит %" }]}
          >
            <InputNumber min={1} max={100} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="upperLimitPercent"
            label="Верхний лимит % (допуск вверх от ожидаемого)"
            rules={[{ required: true, message: "Введите верхний лимит %" }]}
          >
            <InputNumber min={1} max={100} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="hideCableWeight"
            label="Скрыть расч. массу кабеля в прайс-листе (АВВГ, АППВ, АПУНП, АПВ и т.п.)"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
